import { cn } from "../../lib/utils";

/**
 * ESQUELETO DE CARREGAMENTO — o que substitui "Carregando…" solto.
 *
 * 🔴 Por que (auditoria de CX, 29/07, item B7): texto cinza centrado num cartão
 * vazio LÊ COMO TRAVAMENTO. Esqueleto lê como carregamento — a tela já mostra
 * onde as coisas vão nascer, e o dono espera em vez de recarregar a página.
 *
 * Regra de uso: o esqueleto imita o BLOCO que está carregando (três cartões
 * viram três cartões, uma lista vira linhas). Um retângulo genérico gigante é
 * quase tão ruim quanto o texto — promete uma coisa e entrega outra.
 *
 * `TableSkeleton` continua sendo o certo DENTRO de `<table>` (ele emite `<tr>`).
 * Este aqui é pro resto: cartões, listas de `<div>`, painéis.
 */
export function Esqueleto({ className }: { className?: string }) {
  return <div aria-hidden className={cn("animate-pulse rounded-md bg-[hsl(var(--paper-2))]", className)} />;
}

/**
 * Linhas empilhadas com larguras alternadas — o formato de uma lista de itens.
 * `role="status"` + rótulo invisível: quem usa leitor de tela ouve "Carregando",
 * que o esqueleto sozinho não diz.
 */
export function EsqueletoLista({
  linhas = 4,
  alturaLinha = "h-12",
  className,
  rotulo = "Carregando…",
  mudo = false,
}: {
  linhas?: number;
  alturaLinha?: string;
  className?: string;
  rotulo?: string;
  /**
   * Sem `role="status"` nem rótulo. Use quando este esqueleto é o SEGUNDO bloco
   * da mesma espera — dois anúncios pro leitor de tela sobre a mesma consulta é
   * ruído, não acessibilidade.
   */
  mudo?: boolean;
}) {
  return (
    <div className={cn("space-y-2", className)} role={mudo ? undefined : "status"} aria-live={mudo ? undefined : "polite"}>
      {!mudo && <span className="sr-only">{rotulo}</span>}
      {Array.from({ length: linhas }).map((_, i) => (
        <Esqueleto key={i} className={cn(alturaLinha, i % 3 === 2 ? "opacity-70" : "")} />
      ))}
    </div>
  );
}

/** Cartões lado a lado — pro topo de tela que carrega KPIs/placares. */
export function EsqueletoCartoes({
  n = 3,
  altura = "h-24",
  className,
  rotulo = "Carregando…",
}: {
  n?: number;
  altura?: string;
  className?: string;
  rotulo?: string;
}) {
  return (
    <div className={cn("grid gap-3", className)} role="status" aria-live="polite">
      <span className="sr-only">{rotulo}</span>
      {Array.from({ length: n }).map((_, i) => (
        <Esqueleto key={i} className={cn(altura, "rounded-lg")} />
      ))}
    </div>
  );
}
